import { API_BASE_URL } from './config.js';
import fetchWrapper from '../components/utility/fetchWrapper.js'
import { readApiError } from '../utils/parseApiErrorMessage.js';

export const logsService = {
  // Get onboarded logs using correct DynamoDB endpoint
  getOnboardedLogs: async (awsAccountId, clientId) => {
    if (!clientId) {
      console.error("Client ID is required for getOnboardedLogs");
      throw new Error("Client ID is required.");
    }
    try {
      // Use the correct endpoint: /api/v1/logging/configs/{client_id}/awsid-{aws_account_id}
      const response = await fetchWrapper(
        `${API_BASE_URL}/api/v1/logging/configs/${clientId}/awsid-${awsAccountId}`
      );
      if (!response.ok) {
          if (response.status === 404) {
              console.log(`No logs found for ${clientId} / ${awsAccountId}`);
              return [];
          }
          throw new Error(`Failed to fetch logs (Status: ${response.status})`);
      }
      const data = await response.json();
      console.log('[AWS Logs] Raw API response:', data); // Debug logging
      console.log('[AWS Logs] Response type:', typeof data, 'Is array:', Array.isArray(data));
      
      // The endpoint returns { client_id, aws_account_id, log_selector: [{source, arn}, ...] }
      // Transform it to match the expected format: [{ client_id, aws_account_id, log_selector: [...] }]
      if (data && Array.isArray(data.log_selector) && data.log_selector.length > 0) {
        const transformed = [{
          client_id: data.client_id || clientId,
          aws_account_id: data.aws_account_id || awsAccountId,
          log_selector: data.log_selector
        }];
        console.log('[AWS Logs] Transformed data:', transformed); // Debug logging
        console.log('[AWS Logs] Number of logs:', data.log_selector.length);
        return transformed;
      }
      
      console.log('[AWS Logs] No log_selector found or empty array'); // Debug logging
      console.log('[AWS Logs] Data structure:', JSON.stringify(data, null, 2));
      return [];

    } catch (error) {
      console.error("Error in getOnboardedLogs:", error);
      throw error;
    }
  }, 

  // Create/Update logs using correct DynamoDB endpoint
  createLogs: async (payload) => {
    try {
      // Transform payload to match the API schema
      const transformedPayload = {
        client_id: payload.client_id,
        aws_account_id: payload.aws_account_id,
        source: payload.source,
        log_selector: payload.log_selector || payload.log_groups || [] // Support both field names
      };

      console.log("createLogs - Original payload:", payload);
      console.log("createLogs - Transformed payload:", transformedPayload);

      const response = await fetchWrapper(`${API_BASE_URL}/api/v1/logging/onboard`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(transformedPayload),
      });
      
      console.log("createLogs - Response status:", response.status);
      
      if (!response.ok) {
        throw await readApiError(response, "Failed to onboard logs");
      }
      const result = await response.json();
      console.log("createLogs - Success response:", result);
      return result;
    } catch (error) {
      console.error("Error in createLogs:", error);
      throw error;
    }
  },

  // Delete logs using correct DynamoDB endpoint
  deleteLogs: async (deletePayload) => {
    try {
      const response = await fetchWrapper(`${API_BASE_URL}/api/v1/logging/deboard`, {
        method: "DELETE",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(deletePayload),
      });
      if (!response.ok) {
        throw await readApiError(response, "Failed to deboard logs");
      }
       if (response.status === 204) {
           return { success: true, message: "Logs deboarded successfully." };
       }
      return await response.json();
    } catch (error) {
      console.error("Error in deleteLogs:", error);
      throw error;
    }
  },

  // Get Databricks logs
  getDatabricksLogs: async (workspaceName) => {
    try {
      const response = await fetchWrapper(
        `${API_BASE_URL}/databricks/logs/${encodeURIComponent(workspaceName)}`
      );
      if (!response.ok) {
        if (response.status === 404) {
          return null;
        }
        throw new Error(`Failed to fetch Databricks logs (Status: ${response.status})`);
      }
      const data = await response.json();
      return data;
    } catch (error) {
      console.error("Error in getDatabricksLogs:", error);
      throw error;
    }
  },

  // Get all Databricks logs
  getAllDatabricksLogs: async () => {
    try {
      const response = await fetchWrapper(
        `${API_BASE_URL}/databricks/logs`
      );
      if (!response.ok) {
        throw new Error(`Failed to fetch all Databricks logs (Status: ${response.status})`);
      }
      const data = await response.json();
      return data;
    } catch (error) {
      console.error("Error in getAllDatabricksLogs:", error);
      throw error;
    }
  },

};